import { useState } from "react";

import { api } from "../api";
import { Alert, Avatar, Badge, Button, ContactLink, Eyebrow, Icon, Text, Textarea } from "../ds";
import { fechaHora, hace, linkInstagram, linkWhatsapp, rating, sinProtocolo, telefono } from "../formato";
import { Enlace } from "../router";
import { useSesion } from "../sesion";
import type { MensajeCola, ModoEnvio, ResultadoEnvio } from "../tipos";
import { avisoEnvio, useAccion } from "./acciones";
import { TextoMensaje } from "./TextoMensaje";
import s from "./TarjetaMensaje.module.css";

export function TarjetaMensaje({
  m,
  modo,
  plantilla,
  onCambio,
}: {
  m: MensajeCola;
  modo: ModoEnvio;
  /** La apertura elegida en la cola; null en texto libre. */
  plantilla: string | null;
  onCambio: () => void;
}) {
  const { sesion } = useSesion();
  const { pendiente, ejecutar } = useAccion();
  const [editando, setEditando] = useState(false);
  const [texto, setTexto] = useState(m.cuerpo);
  const i = m.inmobiliaria;
  const chatwoot = sesion?.integraciones.chatwoot;
  const puedeEnviar = Boolean(chatwoot?.configurado && chatwoot.bandeja);
  const pendienteEnvio = m.estado === "borrador" || m.estado === "aprobado";
  const lugar = [i.localidad, i.zona && i.zona !== i.localidad ? `zona ${i.zona}` : null].filter(Boolean).join(" · ");

  const accion = (clave: string, camino: string, exito: string, cuerpo?: unknown) =>
    ejecutar(clave, () => api(camino, { metodo: cuerpo === undefined ? "POST" : "PATCH", cuerpo }), exito).then((r) => {
      if (r !== undefined) onCambio();
      return r;
    });

  const enviar = () =>
    ejecutar("enviar", () => api<ResultadoEnvio>(`/api/mensajes/${m.id}/enviar`, { metodo: "POST", cuerpo: { plantilla } }), avisoEnvio(i.nombre)).then((r) => {
      if (r !== undefined) onCambio();
    });
  const guardarTexto = async () => {
    const r = await accion("editar", `/api/mensajes/${m.id}`, "Texto guardado", { cuerpo: texto });
    if (r !== undefined) setEditando(false);
  };

  return (
    <article className={s.tarjeta} data-estado={m.estado}>
      <div className={s.cuerpo}>
        <div className={s.identidad}>
          <div className={s.nombre}>
            <Avatar nombre={i.nombre} size="md" decorative />
            <div className={s.textos}>
              <Enlace href={`/leads/${i.id}`} className={s.enlace}>
                {i.nombre}
              </Enlace>
              {lugar ? (
                <Text as="span" size="xs" tone="muted" className={s.lugar}>
                  <Icon name="map-pin" size={14} />
                  {lugar}
                </Text>
              ) : null}
            </div>
          </div>
          <div className={s.badges}>
            <Badge tone="brand" size="sm">
              Puntaje <span className={s.mono}>{i.puntaje ?? "–"}</span>
            </Badge>
            {i.resenas ? (
              <Badge tone="neutral" size="sm">
                <span className={s.mono}>{rating(i.rating)}</span> · {i.resenas} reseñas
              </Badge>
            ) : null}
          </div>
          <ul className={s.contacto}>
            <li>
              <ContactLink href={linkWhatsapp(m.destinatario)}>
                <span className={s.mono}>{telefono(m.destinatario)}</span>
              </ContactLink>
            </li>
            {i.email ? (
              <li>
                <ContactLink href={`mailto:${i.email}`}>{i.email}</ContactLink>
              </li>
            ) : null}
            {i.sitioWeb ? (
              <li>
                <ContactLink href={i.sitioWeb}>{sinProtocolo(i.sitioWeb)}</ContactLink>
              </li>
            ) : null}
            {i.instagram ? (
              <li>
                <ContactLink href={linkInstagram(i.instagram)}>@{i.instagram}</ContactLink>
              </li>
            ) : null}
          </ul>
        </div>

        <div className={s.mensaje}>
          <div className={s.meta}>
            <Eyebrow>
              {m.secuencia === "wa_v1" ? "Primer mensaje" : m.secuencia} · paso {m.paso}
            </Eyebrow>
            <Text as="span" size="xs" tone="muted">
              {m.estado === "enviado" ? `Enviado ${hace(m.enviadoEn)}` : `Preparado el ${fechaHora(m.creadoEn)}`}
              {m.generadoPor.startsWith("claude") ? " · redactado con Claude" : ""}
            </Text>
          </div>
          {editando ? (
            <div className={s.edicion}>
              <Textarea aria-label="Texto del mensaje" value={texto} onChange={(e) => setTexto(e.target.value)} rows={9} maxLength={4000} showCount />
              <div className={s.fila}>
                <Button size="sm" variant="ghost" onClick={() => (setTexto(m.cuerpo), setEditando(false))}>
                  Cancelar
                </Button>
                <Button size="sm" icon="check" pending={pendiente === "editar"} onClick={guardarTexto} disabled={texto.trim() === m.cuerpo.trim()}>
                  Guardar texto
                </Button>
              </div>
            </div>
          ) : (
            <TextoMensaje segmentos={m.segmentos} />
          )}
          {m.error && pendienteEnvio ? (
            <Alert tone="danger" compact>
              El último intento falló: {m.error}
            </Alert>
          ) : null}
        </div>
      </div>

      <footer className={s.acciones}>
        {pendienteEnvio ? (
          <>
            <Button
              icon="send"
              size="sm"
              pending={pendiente === "enviar" || m.estado === "aprobado"}
              disabled={!puedeEnviar || editando}
              onClick={enviar}
              title={puedeEnviar ? undefined : "Falta configurar Chatwoot (ver Configuración)"}
            >
              {modo === "plantilla" ? "Enviar plantilla" : "Enviar por Chatwoot"}
            </Button>
            {modo === "texto_libre" || !puedeEnviar ? (
              <Button size="sm" variant="outline" iconAfter="external-link" href={linkWhatsapp(m.destinatario, m.cuerpo)} target="_blank">
                Abrir en wa.me
              </Button>
            ) : null}
            <Button size="sm" variant="outline" icon="check" pending={pendiente === "marcar"} onClick={() => accion("marcar", `/api/mensajes/${m.id}/marcar-enviado`, "Marcado como enviado")}>
              Marcar enviado
            </Button>
            {modo === "texto_libre" && !editando ? (
              <Button size="sm" variant="ghost" icon="pencil" onClick={() => setEditando(true)}>
                Editar
              </Button>
            ) : null}
            <span className={s.espacio} />
            <Button size="sm" variant="danger-soft" icon="x" pending={pendiente === "descartar"} onClick={() => accion("descartar", `/api/mensajes/${m.id}/descartar`, "Descartado")}>
              Descartar
            </Button>
          </>
        ) : (
          <>
            <Badge tone="neutral" size="md" dot>
              {m.error === "descartado en la cola" ? "Descartado en la cola" : `Cancelado: la inmobiliaria pasó a ${i.estado}`}
            </Badge>
            <span className={s.espacio} />
            {["calificada", "contactada"].includes(i.estado) ? (
              <Button size="sm" variant="ghost" icon="rotate-ccw" pending={pendiente === "restaurar"} onClick={() => accion("restaurar", `/api/mensajes/${m.id}/restaurar`, "Volvió a la cola")}>
                Restaurar
              </Button>
            ) : null}
          </>
        )}
      </footer>
    </article>
  );
}
